import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, FileText, DollarSign, TrendingUp, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F4C5C]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          Admin Dashboard
        </h1>
        <p className="text-[#6B7280]">Overview of clinic operations</p>
      </div>

      {/* Stats Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm" data-testid="stat-total-patients">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-[#0F4C5C]/10 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-[#0F4C5C]" />
            </div>
          </div>
          <div className="text-3xl font-bold text-[#1F2937]">{stats?.total_patients || 0}</div>
          <div className="text-sm text-[#6B7280]">Total Patients</div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm" data-testid="stat-total-treatments">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-[#98D8CA]/20 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-[#0F4C5C]" />
            </div>
          </div>
          <div className="text-3xl font-bold text-[#1F2937]">{stats?.total_treatments || 0}</div>
          <div className="text-sm text-[#6B7280]">Total Treatments</div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm" data-testid="stat-total-revenue">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="text-3xl font-bold text-[#1F2937]">${stats?.total_revenue?.toFixed(2) || '0.00'}</div>
          <div className="text-sm text-[#6B7280]">Total Revenue</div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm" data-testid="stat-pending-payments">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <div className="text-3xl font-bold text-[#1F2937]">${stats?.pending_payments?.toFixed(2) || '0.00'}</div>
          <div className="text-sm text-[#6B7280]">Pending Payments</div>
        </div>
      </div>

      {/* Recent Patients */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
        <h2 className="text-xl font-bold text-[#1F2937] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
          Recent Patient Registrations
        </h2>
        {stats?.recent_patients && stats.recent_patients.length > 0 ? (
          <div className="space-y-3">
            {stats.recent_patients.map((patient, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-[#F9FAFB] rounded-lg" data-testid={`recent-patient-${index}`}>
                <div>
                  <div className="font-semibold text-[#1F2937]">{patient.name}</div>
                  <div className="text-sm text-[#6B7280]">{patient.email}</div>
                </div>
                <div className="flex items-center text-sm text-[#6B7280]">
                  <Calendar className="w-4 h-4 mr-1" />
                  {new Date(patient.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#6B7280] text-center py-8">No recent registrations</p>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;